/**
 * Rows of the manufactures export (CSV / Excel), one per manufacture.
 *
 * Names are the snapshots the manufacture stored when it was saved, so after a product is renamed
 * they no longer find it. The ids are what a spreadsheet or script can look a product up by: the
 * produced product's `product.id` and each ingredient's id are the same id the products export
 * writes as its ID column (ATL-1900). Both read modes answer it: a Firestore manufacture stores the
 * product snapshot with its Firestore id, and the backend returns the product's Firestore id.
 *
 * Values are written as they are; `exportToCSV` quotes a comma (ATL-1899).
 */

import { format } from 'date-fns'
import { getLocalStoreDate } from './timezoneFunctions'

const toDate = (value) => {
  if (!value) return null
  if (value.toDate) return value.toDate()
  if (value instanceof Date) return value
  if (typeof value === 'string') return new Date(value)
  return null
}

const ingredientId = (ingredient) => ingredient.id || ingredient.productId || ''

export const manufactureExportRows = (items, t, { storesDict, usersDict }) =>
  items.map((manufacture) => {
    const date = toDate(manufacture.date)
    const manufactureDate = date ? format(getLocalStoreDate(manufacture.store, date), 'd/M/y HH:mm') : ''

    const storeName = storesDict?.[manufacture.store]?.name || manufacture.store || ''
    const cashierName = usersDict?.[manufacture.cashierUID]?.name || manufacture.cashierUID || ''

    const ingredients = manufacture.ingredients || []
    // Ingredients with their costs: "name: qty x price = total; ..."
    const ingredientsList = ingredients.map((ing) => {
      const qty = ing.quantity || 0
      const unitPrice = ing.purchasePrice || 0
      return `${ing.name || ''}: ${qty} x ${unitPrice} = ${(qty * unitPrice).toFixed(2)}`
    }).join('; ')
    const ingredientUnitCosts = ingredients.map((ing) => `${ing.name || ''}: ${ing.purchasePrice || 0}`).join('; ')
    // Ingredients by id: "id: qty; ...", qty per produced unit as the recipe stores it (the header
    // says so). An id holds no ':' or ';', so the cell splits reliably.
    const ingredientIds = ingredients.map((ing) => `${ingredientId(ing)}: ${ing.quantity || 0}`).join('; ')
    const ingredientsTotalCost = ingredients.reduce(
      (sum, ing) => sum + ((ing.quantity || 0) * (ing.purchasePrice || 0)),
      0
    )

    return {
      [t('manufactures.manufactureId')]: manufacture.id || '',
      [t('date')]: manufactureDate,
      [t('store')]: storeName,
      [t('manufactures.cashier')]: cashierName,
      [t('product')]: manufacture.product?.name || '',
      [t('manufactures.productId')]: manufacture.product?.id || '',
      [t('manufactures.quantity')]: manufacture.quantity || 0,
      [t('manufactures.batchNumber')]: manufacture.batchNumber || '',
      [t('manufactures.ingredientUnitCosts')]: ingredientUnitCosts,
      [t('manufactures.ingredients')]: ingredientsList,
      [t('manufactures.ingredientIds')]: ingredientIds,
      [t('manufactures.ingredientsCost')]: ingredientsTotalCost.toFixed(2),
      [t('manufactures.totalSum')]: manufacture.totalSum || 0,
    }
  })
